import * as React from "react";
import AsyncSelect from "react-select/async";
import { X, Package2, Check, Search, Plus, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent } from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { cn } from "@/lib/utils";
import * as productService from '@features/products/services/productService';

// Custom styles for react-select to match shadcn UI
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "40px",
    border: "2px solid hsl(var(--border))",
    borderRadius: "var(--radius)",
    backgroundColor: "hsl(var(--background))",
    boxShadow: state.isFocused ? "0 0 0 2px hsl(var(--primary) / 0.2)" : "none",
    borderColor: state.isFocused ? "hsl(var(--primary))" : "hsl(var(--border))",
    "&:hover": {
      borderColor: "hsl(var(--primary) / 0.5)",
    },
    transition: "all 0.2s",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "hsl(var(--secondary))",
    borderRadius: "var(--radius)",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "hsl(var(--secondary-foreground))",
    fontSize: "0.875rem",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "hsl(var(--secondary-foreground))",
    "&:hover": {
      backgroundColor: "hsl(var(--destructive))",
      color: "hsl(var(--destructive-foreground))",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "hsl(var(--muted-foreground))",
  }),
  input: (provided) => ({
    ...provided,
    color: "hsl(var(--foreground))",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "hsl(var(--popover))",
    border: "2px solid hsl(var(--border))",
    borderRadius: "var(--radius)",
    boxShadow: "0 10px 15px -3px hsl(var(--background) / 0.1), 0 4px 6px -2px hsl(var(--background) / 0.05)",
    zIndex: 1000,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "hsl(var(--primary) / 0.2)"
      : state.isFocused
      ? "hsl(var(--primary) / 0.1)"
      : "transparent",
    color: "hsl(var(--foreground))",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "hsl(var(--primary) / 0.1)",
    },
  }),
  loadingMessage: (provided) => ({
    ...provided,
    color: "hsl(var(--muted-foreground))",
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    color: "hsl(var(--muted-foreground))",
  }),
};

export function MultiSelectProduct({
  products = [],
  onSelect,
  className,
  placeholder,
  maxDisplayed = 10,
  disabled = false,
  showQuantity = true,
  defaultQuantity = 1,
}) {
  const { t } = useTranslation();
  const [selectedProducts, setSelectedProducts] = React.useState(
    products?.map(product => ({
      ...product,
      quantity: product.quantity || defaultQuantity
    })) || []
  );

  React.useEffect(() => {
    setSelectedProducts(
      products?.map(product => ({
        ...product,
        quantity: product.quantity || defaultQuantity
      })) || []
    );
  }, [products, defaultQuantity]);

  // Load options function for react-select async
  const loadOptions = React.useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 1) {
      // Load last 10 products when no input
      try {
        const params = new URLSearchParams({
          isActive: "true",
          limit: maxDisplayed,
          page: "1",
          sort: "-createdAt", // Sort by creation date descending to get latest products
        });
        const response = await productService.fetchProducts(params.toString());
        return (response?.data || []).map((product) => ({
          value: product._id,
          label: `${product.name} (${product.stock || 0} ${t("main.inStock")})`,
          product: product,
        }));
      } catch (error) {
        console.error("Error loading default products:", error);
        return [];
      }
    }

    try {
      const params = new URLSearchParams({
        name_q: inputValue,
        isActive: "true",
        limit: "50",
        page: "1",
      });
      const response = await productService.fetchProducts(params.toString());
      
      return (response?.data || []).map((product) => ({
        value: product._id,
        label: `${product.name} (${product.stock || 0} ${t("main.inStock")})`,
        product: product,
      }));
    } catch (error) {
      console.error("Error loading products:", error);
      return [];
    }
  }, [t]);

  const handleChange = React.useCallback((selectedOptions) => {
    const newProducts = selectedOptions?.map((option) => {
      // Check if this product is already selected to preserve quantity
      const existingProduct = selectedProducts.find(p => p._id === option.product._id);
      return {
        ...option.product,
        quantity: existingProduct ? existingProduct.quantity : defaultQuantity
      };
    }) || [];
    setSelectedProducts(newProducts);
    onSelect(newProducts);
  }, [selectedProducts, defaultQuantity, onSelect]);

  const handleQuantityChange = React.useCallback((productId, newQuantity) => {
    if (newQuantity < 1) return; // Prevent negative or zero quantities
    
    setSelectedProducts(prevProducts => {
      const updatedProducts = prevProducts.map(product => 
        product._id === productId 
          ? { ...product, quantity: parseInt(newQuantity) || 1 }
          : product
      );
      // Call onSelect with updated products
      onSelect(updatedProducts);
      return updatedProducts;
    });
  }, [onSelect]);

  const handleIncreaseQuantity = React.useCallback((productId) => {
    setSelectedProducts(prevProducts => {
      const product = prevProducts.find(p => p._id === productId);
      if (product && product.quantity < (product.stock || 999)) {
        const updatedProducts = prevProducts.map(p => 
          p._id === productId 
            ? { ...p, quantity: p.quantity + 1 }
            : p
        );
        onSelect(updatedProducts);
        return updatedProducts;
      }
      return prevProducts;
    });
  }, [onSelect]);

  const handleDecreaseQuantity = React.useCallback((productId) => {
    setSelectedProducts(prevProducts => {
      const product = prevProducts.find(p => p._id === productId);
      if (product && product.quantity > 1) {
        const updatedProducts = prevProducts.map(p => 
          p._id === productId 
            ? { ...p, quantity: p.quantity - 1 }
            : p
        );
        onSelect(updatedProducts);
        return updatedProducts;
      }
      return prevProducts;
    });
  }, [onSelect]);

  const handleRemoveProduct = React.useCallback((productToRemove) => {
    setSelectedProducts(prevProducts => {
      const newProducts = prevProducts.filter((p) => p._id !== productToRemove._id);
      onSelect(newProducts);
      return newProducts;
    });
  }, [onSelect]);

  const handleClearAll = React.useCallback(() => {
    setSelectedProducts([]);
    onSelect([]);
  }, [onSelect]);

  const totalQuantity = React.useMemo(() => {
    return selectedProducts.reduce((total, product) => total + (product.quantity || 1), 0);
  }, [selectedProducts]);

  const selectedOptions = React.useMemo(() => {
    return selectedProducts.map((product) => ({
      value: product._id,
      label: `${product.name} (${product.stock || 0} ${t("main.inStock")})`,
      product: product,
    }));
  }, [selectedProducts, t]);

  const ProductCard = React.memo(({ product, onQuantityChange, onIncrease, onDecrease, onRemove, showQuantity, t }) => (
    <Card key={product._id} className="border-primary/20 bg-primary/5">
      <CardContent className="p-3">
        <div className="flex items-center gap-3 rtl-text ltr-text">
          <div className="p-2 bg-primary/10 rounded-full">
            <Package2 className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h5 className="font-medium text-foreground text-sm">
                {product.name}
              </h5>
              <Check className="h-3 w-3 text-primary" />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary" className="text-xs">
                {t("products.stock")}: {product.stock || 0}
              </Badge>
              {product.price && (
                <Badge variant="outline" className="text-xs">
                  {product.price} {t("main.currency")}
                </Badge>
              )}
            </div>
            {showQuantity && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {t("main.quantity")}:
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDecrease(product._id)}
                    disabled={product.quantity <= 1}
                    className="h-6 w-6 p-0 hover:bg-destructive/10"
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <Input
                    type="number"
                    value={product.quantity || 1}
                    onChange={(e) => onQuantityChange(product._id, e.target.value)}
                    className="h-6 w-fit text-center text-xs px-1"
                    min="1"
                    // max={product.stock || 999}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onIncrease(product._id)}
                    disabled={product.quantity >= (product.stock || 999)}
                    className="h-6 w-6 p-0 hover:bg-primary/10"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(product)}
            className="h-6 w-6 p-0 hover:bg-destructive/10 ltr:ml-auto rtl:mr-auto"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  ));

  return (
    <div className={cn("space-y-4", className)} dir="auto">
      <div className="space-y-2">
        <div className="rtl-text ltr-text">
          <AsyncSelect
            isMulti
            value={selectedOptions}
            onChange={handleChange}
            loadOptions={loadOptions}
            placeholder={
              placeholder ||
              t("action.select", {
                entity: t("products.products"),
              })
            }
            noOptionsMessage={({ inputValue }) =>
              !inputValue || inputValue.length < 1
                ? t("action.typeToSearch")
                : t("main.notFound", {
                    entity: t("products.products"),
                  })
            }
            loadingMessage={() => t("main.loading")}
            styles={customStyles}
            isDisabled={disabled}
            isClearable
            cacheOptions
            defaultOptions={true}
            className="w-full"
            classNamePrefix="react-select"
          />
        </div>

        {selectedProducts.length > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground rtl-text ltr-text">
            <span>
              {selectedProducts.length} {t("products.product")}
              {selectedProducts.length !== 1 ? "s" : ""} {t("main.selected")}
              {showQuantity && (
                <span className="ltr:ml-2 rtl:mr-2">
                  ({totalQuantity} {t("main.total")} {t("main.items")})
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-6 text-xs"
            >
              {t("action.clearAll")}
            </Button>
          </div>
        )}
      </div>

      {selectedProducts.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">
            {t("products.selectedProducts")}:
          </h4>
          <div className="grid gap-2">
            {selectedProducts.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                onQuantityChange={handleQuantityChange}
                onIncrease={handleIncreaseQuantity}
                onDecrease={handleDecreaseQuantity}
                onRemove={handleRemoveProduct}
                showQuantity={showQuantity}
                t={t}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
